"use client"
import { useState, useEffect, Component, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { logout } from "../store/slices/authSlice"
import { fetchStudents } from "../store/slices/studentSlice"
import { fetchFeeStats } from "../store/slices/feeSlice"
import StudentsComponent from "./StudentsComponent"
import FeesComponent from "./FeesComponent"
import ResultsComponent from "./ResultsComponent"
import DocumentsComponent from "./DocumentsComponent"
import DepartmentComponent from "./DepartmentComponent"
import UserManagement from "./UserManagement"
import FacultyComponent from "./FacultyComponent"
import axios from "axios"
import * as XLSX from "xlsx"
import {
  Users,
  CreditCard,
  FolderOpen,
  LogOut,
  GraduationCap,
  BarChart3,
  MenuIcon,
  BookOpen,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Download,
} from "lucide-react"
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js"
import { Pie, Bar } from "react-chartjs-2"
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Divider,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material"
import { useTheme, useMediaQuery } from "@mui/material"

// Register Chart.js elements
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// --- Define a modern, vibrant color palette ---
const PRIMARY_COLOR = "#007bff"
const ACCENT_COLOR = "#28a745"
const WARNING_COLOR = "#ffc107"
const DANGER_COLOR = "#dc3545"
const INFO_COLOR = "#17a2b8"

// --- Enhanced Chart Colors for better contrast ---
const PIE_COLORS = [PRIMARY_COLOR, ACCENT_COLOR, WARNING_COLOR, DANGER_COLOR]
const BAR_COLORS = [PRIMARY_COLOR, DANGER_COLOR, INFO_COLOR, ACCENT_COLOR]

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="text-red-500 text-center p-4 bg-red-50 rounded-lg m-4">
          Error in **{this.props.componentName}**. Please check the console for details.
        </div>
      )
    }
    return this.props.children
  }
}

const API_URL = process.env.REACT_APP_API_URL

// =================================================================
// --- ReportModal Component with Table and Excel Download ---
// =================================================================
const ReportModal = ({ open, handleClose, reportType, reportData, formatCurrency }) => {
  // Fallback to empty arrays if reportData is undefined
  const instituteData = reportData[reportType]?.institute_summary || []
  const tradeData = reportData[reportType]?.trade_summary || []

  const handleDownloadExcel = () => {
    // Prepare data for Excel
   const instituteSheetData = instituteData.map(item => {
  console.log("Institute Item:", item) // 👈 this logs the full item object

  return {
    "Institute": item.department_name || "N/A",
    "Collected Amount (INR)": formatCurrency(item.total_collected),
    "Due Amount (INR)": formatCurrency(item.total_pending),
  }
})
    const tradeSheetData = tradeData.map(item => {
      console.log("Institute Item:", item) 
      return {
      "Trade": item.trade_name || "N/A",
      "Collected Amount (INR)": formatCurrency(item.total_collected),
      "Due Amount (INR)": formatCurrency(item.total_pending),
      }
    });

    // Create workbook and worksheets
    const workbook = XLSX.utils.book_new();
    
    // Institute-wise sheet
    const instituteSheet = XLSX.utils.json_to_sheet(instituteSheetData);
    XLSX.utils.book_append_sheet(workbook, instituteSheet, "Institute-wise");

    // Trade-wise sheet
    const tradeSheet = XLSX.utils.json_to_sheet(tradeSheetData);
    XLSX.utils.book_append_sheet(workbook, tradeSheet, "Trade-wise");

    // Write and download Excel file
    XLSX.writeFile(workbook, `${reportType}_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  // Show loading or error state if data is empty
  if (!instituteData.length) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="report-modal-title"
        aria-describedby="report-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500, md: 700 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            outline: "none",
          }}
        >
          <Typography
            id="report-modal-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: "bold", mb: 1, color: PRIMARY_COLOR }}
          >
            {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No data available for the selected period.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="report-modal-title"
      aria-describedby="report-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 500, md: 700 },
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          outline: "none",
        }}
      >
        <Typography
          id="report-modal-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: "bold", mb: 1, color: PRIMARY_COLOR }}
        >
          {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Report generated for the period: <strong>{reportData[reportType]?.dateRange || "N/A"}</strong>
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Department</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }} align="right">Collected Fees</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }} align="right">Due Fees</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instituteData.map((dept, index) => (
                <TableRow key={index}>
                  <TableCell>{dept.department_name || "N/A"}</TableCell>
                  <TableCell align="right" sx={{ color: ACCENT_COLOR, fontWeight: "bold" }}>
                    {formatCurrency(dept.total_collected)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: DANGER_COLOR, fontWeight: "bold" }}>
                    {formatCurrency(dept.total_pending)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadExcel}
            startIcon={<Download size={18} />}
            sx={{ bgcolor: ACCENT_COLOR, "&:hover": { bgcolor: "#1e7e34" } }}
          >
            Download Excel
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
// =================================================================
// --- END ReportModal Component ---
// =================================================================

const AdminDashboard = () => {
  const dispatch = useDispatch()
  const { user, loading: authLoading } = useSelector((state) => state.auth)
  const {
    students: allStudentsList,
    loading: studentsLoading,
    error: studentsError,
  } = useSelector((state) => state.students)
  const { stats, loading: feesLoading, error: feesError } = useSelector((state) => state.fees)
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem("activeTab") || "overview")
  const [mobileOpen, setMobileOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  const [reportRange, setReportRange] = useState("monthly")
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [reportModalData, setReportModalData] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState(null)
  const [dashboardOverview, setDashboardOverview] = useState(null)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState(null)
  const [feesReport, setFeesReport] = useState({ department_summary: [], daily_summary: [], trade_summary: [] })
  const [enrollmentsReport, setEnrollmentsReport] = useState({ enrollments: [], summary: null })

  const handleLogout = () => {
    localStorage.removeItem("activeTab")
    setActiveTab("overview")
    dispatch(logout())
  }

  const handleOpenReportModal = async () => {
    try {
      setReportError(null)
      setReportLoading(true)
      const token = localStorage.getItem("token")
      const headers = { Authorization: `Bearer ${token}` }
      const { start, end } = getDateRange(reportRange)

      // Fetch filtered data
      const [feesFiltered] = await Promise.all([
  axios.get(`${API_URL}/reports/fees`, {
    headers,
    params: { start_date: start, end_date: end },
  }),
])

// ✅ Log full backend response for debugging
console.log("Fees API full response:", feesFiltered?.data)

// ✅ Optional: log only the relevant array (if nested)
console.log("Fees Report Data:", feesFiltered?.data?.data)

      const feesData = feesFiltered?.data?.data

      setReportModalData({
        dateRange: `${start} to ${end}`,
        institute_summary: feesData?.department_summary || [],
        trade_summary: feesData?.trade_summary || [],
      })
      setReportModalOpen(true)
    } catch (err) {
      setReportError(err.response?.data?.message || err.message)
      setReportModalOpen(true) // open to show error
    } finally {
      setReportLoading(false)
    }
  }

  const handleCloseReportModal = () => {
    setReportModalOpen(false)
  }

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab)
  }, [activeTab])

  useEffect(() => {
    dispatch(fetchStudents())
    dispatch(fetchFeeStats())
  }, [dispatch])

  useEffect(() => {
    const fetchFilteredFees = async () => {
      try {
        const token = localStorage.getItem("token")
        const headers = { Authorization: `Bearer ${token}` }
        const { start, end } = getDateRange(reportRange)
        const res = await axios.get(`${API_URL}/reports/fees`, {
          headers,
          params: { start_date: start, end_date: end },
        })
        if (res?.data?.data) setFeesReport(res.data.data)
      } catch (e) {
        console.log("[v0] fees report (filtered) fetch error:", e?.response?.data || e?.message)
      }
    }
    fetchFilteredFees()
  }, [reportRange])

 const tabs = [
    { id: "overview", label: "Dashboard", icon: BarChart3 },
    { id: "students", label: "Students", icon: Users },
    { id: "fees", label: "Fees & Finance", icon: CreditCard },
    { id: "documents", label: "Documents", icon: FolderOpen },
   
  ]


  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "₹0"
    const numericAmount = typeof amount === "string" ? Number.parseFloat(amount.replace(/[^0-9.-]+/g, "")) : amount
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(
      numericAmount,
    )
  }

  const getDateRange = (range) => {
    const end = new Date()
    const start = new Date()
    if (range === "daily") {
      // today
    } else if (range === "weekly") {
      start.setDate(end.getDate() - 6)
    } else if (range === "monthly") {
      start.setMonth(end.getMonth() - 1)
    } else if (range === "yearly") {
      start.setFullYear(end.getFullYear() - 1)
    }
    const pad = (n) => String(n).padStart(2, "0")
    const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    return { start: toISO(start), end: toISO(end) }
  }

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setDashboardLoading(true)
        setDashboardError(null)
        const token = localStorage.getItem("token")
        const headers = { Authorization: `Bearer ${token}` }

        // Fetch fees and enrollments reports
        const feesReq = axios.get(`${API_URL}/reports/fees`, { headers })
        const enrollReq = axios.get(`${API_URL}/reports/enrollments`, { headers })

        // For super admin, also fetch the overview dashboard
        const dashboardReq =
          user?.role === "super_admin"
            ? axios.get(`${API_URL}/reports/dashboard`, { headers })
            : Promise.resolve({ data: null })

        const [feesRes, enrollRes, dashRes] = await Promise.all([feesReq, enrollReq, dashboardReq])

        if (feesRes?.data?.data) setFeesReport(feesRes.data.data)
        if (enrollRes?.data?.data) setEnrollmentsReport(enrollRes.data.data)
        if (dashRes?.data?.data?.overview) setDashboardOverview(dashRes.data.data.overview)
      } catch (err) {
        setDashboardError(err.response?.data?.message || err.message)
      } finally {
        setDashboardLoading(false)
      }
    }
    fetchAll()
  }, [dispatch, user?.role])

  const computedDeptCounts = useMemo(() => {
    const map = new Map()
    for (const s of allStudentsList || []) {
      const name = s?.department?.name || "Unknown"
      map.set(name, (map.get(name) || 0) + 1)
    }
    return Array.from(map.entries()).map(([department_name, student_count]) => ({
      department_name,
      student_count,
    }))
  }, [allStudentsList])

  const departmentStatsSource = dashboardOverview?.department_stats?.length
    ? dashboardOverview.department_stats
    : computedDeptCounts

  const feesStatsSource = dashboardOverview?.department_stats?.length
    ? (dashboardOverview.department_stats || []).map((d) => ({
        department_name: d.department_name,
        total_collected: Number(d.fees_collected) || 0,
        total_pending: Number(d.fees_pending) || 0,
      }))
    : feesReport?.department_summary || []

  const departmentStudentData = {
    labels: departmentStatsSource.map((d) => d.department_name),
    datasets: [
      {
        label: "Students",
        data: departmentStatsSource.map((d) => d.student_count ?? 0),
        backgroundColor: BAR_COLORS[0],
        borderRadius: 4,
      },
    ],
  }

  const departmentFeeData = {
    labels: feesStatsSource.map((d) => d.department_name),
    datasets: [
      {
        label: "Collected (₹)",
        data: feesStatsSource.map((d) => Number(d.total_collected) || 0),
        backgroundColor: ACCENT_COLOR,
        borderRadius: 4,
      },
      {
        label: "Pending (₹)",
        data: feesStatsSource.map((d) => Number(d.total_pending) || 0),
        backgroundColor: WARNING_COLOR,
        borderRadius: 4,
      },
    ],
  }

  const feesCollection = dashboardOverview?.fees_collection || {
    total_collected: feesReport?.department_summary?.reduce((s, d) => s + (Number(d.total_collected) || 0), 0),
    total_pending: feesReport?.department_summary?.reduce((s, d) => s + (Number(d.total_pending) || 0), 0),
  }

  const pieData = {
    labels: ["Paid", "Pending"],
    datasets: [
      {
        data: [Number(feesCollection?.total_collected) || 0, Number(feesCollection?.total_pending) || 0],
        backgroundColor: [ACCENT_COLOR, WARNING_COLOR],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  }

  const recentEnrollments =
    dashboardOverview?.recent_enrollments ||
    enrollmentsReport?.enrollments?.map((e) => ({
      date: e.enrollment_date || e.date,
      count: 1,
    })) ||
    []

  // Aggregate by date if needed
  const enrollByDate = recentEnrollments.reduce((acc, item) => {
    const key = item.date?.slice(0, 10)
    acc[key] = (acc[key] || 0) + (item.count || 1)
    return acc
  }, {})
  const enrollDates = Object.keys(enrollByDate).sort()
  const barData = {
    labels: enrollDates,
    datasets: [
      {
        label: "New Enrollments",
        data: enrollDates.map((d) => enrollByDate[d]),
        backgroundColor: PRIMARY_COLOR,
        borderRadius: 4,
      },
    ],
  }

  const enrolledStudent = allStudentsList.filter(
    (s) =>
      String(s.admission_status).toUpperCase() === "APPROVED" ||
      String(s.admission_status).toUpperCase() === "ENROLLED",
  ).length

  const chartOptions = (titleText, legendPosition = "top", scaleOptions = {}) => ({
    responsive: true,
    plugins: {
      legend: { position: legendPosition },
      title: { display: true, text: titleText, font: { size: 16, weight: "bold" } },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" }, ...scaleOptions.y },
      x: { grid: { display: false }, ...scaleOptions.x },
    },
  })

  const feesChartOptions = chartOptions("Institute-wise Fees (₹)", "top")

  const renderOverview = () => {
    const totalStudentsCount = allStudentsList?.length || 0

    const pieOptions = chartOptions("Paid & Pending Chart", "right")
    const barOptions = chartOptions("Performance Comparison", "top")
    const maxStudents = Math.max(0, ...departmentStatsSource.map((d) => d.student_count ?? 0))
    const suggestedMax = maxStudents > 0 ? Math.ceil(maxStudents * 1.2) : 10
    const stepSize = suggestedMax > 0 ? Math.max(1, Math.ceil(suggestedMax / 5)) : 2

    const departmentChartOptions = chartOptions("Student Count per Institute", "top", {
      y: {
        beginAtZero: true,
        suggestedMax,
        grid: { color: "rgba(0, 0, 0, 0.05)" },
        ticks: { stepSize },
      },
      x: { grid: { display: false } },
    })

    return (
      <div className="flex flex-col gap-8">
        {(studentsError || feesError || dashboardError) && (
          <div className="text-red-500 bg-red-100 p-3 rounded-lg flex items-center gap-2">
            <AlertTriangle size={20} />
            {studentsError && <p>Error loading students: {studentsError}</p>}
            {feesError && <p>Error loading fees: {feesError}</p>}
            {dashboardError && <p>Error loading reports: {dashboardError}</p>}
          </div>
        )}

        {/* Header Section */}
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 mb-1">
            {user?.role === "super_admin" ? "Super Admin Dashboard" : "Administrator Dashboard"}
          </h1>
          <p className="text-gray-600 font-medium">
            Welcome back, <span className="text-gray-900 font-bold">{user?.name}</span>. Here is your institution's
            performance overview.
            {user?.role === "super_admin" && (
              <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold">
                SUPER ADMIN
              </span>
            )}
          </p>
        </div>

        {/* Main Stats Cards (Redux Data) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {studentsLoading || feesLoading || dashboardLoading ? (
            <div className="col-span-full text-center p-8 bg-white rounded-xl shadow-lg">
              <CircularProgress sx={{ color: PRIMARY_COLOR, mb: 2 }} />
              <p className="text-gray-600">Loading Stats...</p>
            </div>
          ) : (
            [
              {
                label: "Total Students",
                value: totalStudentsCount,
                icon: <Users size={32} className="text-white" />,
                color: PRIMARY_COLOR,
              },
              {
                label: "Enrolled",
                value: enrolledStudent,
                icon: <CheckCircle size={32} className="text-white" />,
                color: ACCENT_COLOR,
              },
              {
                label: "Collected Amount",
                value: formatCurrency(stats?.total_paid_amount),
                icon: <DollarSign size={32} className="text-white" />,
                color: ACCENT_COLOR,
              },
              {
                label: "Pending Fees",
                value: formatCurrency(stats?.total_pending_amount),
                icon: <AlertTriangle size={32} className="text-white" />,
                color: WARNING_COLOR,
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className="bg-white p-5 rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-0.5 flex items-center justify-between border-l-4"
                style={{ borderColor: card.color }}
              >
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-gray-500 uppercase">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{card.value}</p>
                </div>
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center p-3 shadow-md"
                  style={{ backgroundColor: card.color }}
                >
                  {card.icon}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Fees Collection Summary */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <DollarSign size={20} className="text-green-500" /> Fees Collection Summary
          </h2>
          <Divider sx={{ mb: 3 }} />
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            {[
              {
                type: "Estimated Revenue",
                amount: formatCurrency(stats?.total_paid_amount + stats?.total_pending_amount),
                color: PRIMARY_COLOR,
                icon: <CreditCard size={20} className="text-white" />,
              },
            ].map((item, index) => (
              <div
                key={index}
                className="flex flex-col p-4 bg-gray-50 rounded-lg border-l-4"
                style={{ borderColor: item.color }}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-600">{item.type}</p>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: item.color }}
                  >
                    {item.icon}
                  </div>
                </div>
                <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(item.amount)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Chart Section: Department Stats and Fees by Faculty */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <Bar data={departmentStudentData} options={departmentChartOptions} />
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <Bar data={departmentFeeData} options={feesChartOptions} />
          </div>
        </div>

        {/* Original Chart Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg flex justify-center items-center">
            <div className="w-full max-w-sm">
              <Pie data={pieData} options={pieOptions} />
            </div>
          </div>
          <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>

        {/* Date-wise Reports and User Profile/Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
          {/* Date-wise Reports (Report Generator) */}
          <div
            className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg border-l-4"
            style={{ borderColor: WARNING_COLOR }}
          >
            <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Calendar size={20} className="text-yellow-600" /> Date-wise Reports
            </h3>
            <p className="text-sm text-gray-500 mb-4">Generate comprehensive financial reports.</p>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="report-range-label">Report Range</InputLabel>
              <Select
                labelId="report-range-label"
                value={reportRange}
                label="Report Range"
                onChange={(e) => setReportRange(e.target.value)}
              >
                <MenuItem value="daily">Daily Report</MenuItem>
                <MenuItem value="weekly">Weekly Report</MenuItem>
                <MenuItem value="monthly">Monthly Report</MenuItem>
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              onClick={handleOpenReportModal}
              sx={{
                textTransform: "none",
                bgcolor: WARNING_COLOR,
                color: "#333",
                fontWeight: "bold",
                "&:hover": { bgcolor: "#e6ac00" },
              }}
            >
              Generate {reportRange.charAt(0).toUpperCase() + reportRange.slice(1)} Report
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview()
      case "faculty":
        return (
          <ErrorBoundary componentName="FacultyComponent">
            <FacultyComponent />
          </ErrorBoundary>
        )
      case "department":
        return (
          <ErrorBoundary componentName="DepartmentComponent">
            <DepartmentComponent />
          </ErrorBoundary>
        )
      case "students":
        return (
          <ErrorBoundary componentName="StudentsComponent">
            <StudentsComponent />
          </ErrorBoundary>
        )
      case "fees":
        return (
          <ErrorBoundary componentName="FeesComponent">
            <FeesComponent />
          </ErrorBoundary>
        )
      case "results":
        return (
          <ErrorBoundary componentName="ResultsComponent">
            <ResultsComponent />
          </ErrorBoundary>
        )
      case "documents":
        return (
          <ErrorBoundary componentName="DocumentsComponent">
            <DocumentsComponent />
          </ErrorBoundary>
        )
      case "user-management":
        return (
          <ErrorBoundary componentName="UserManagement">
            <UserManagement />
          </ErrorBoundary>
        )
      default:
        return renderOverview()
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress sx={{ color: PRIMARY_COLOR, mb: 2 }} />
          <p className="text-gray-600">Loading Dashboard...</p>
        </div>
      </div>
    )
  }

  const drawerContent = (
    <div className="p-4 h-full flex flex-col justify-between">
      <List>
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isSelected = activeTab === tab.id
          return (
            <ListItem key={tab.id} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  setActiveTab(tab.id)
                  if (isMobile) setMobileOpen(false)
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  "&.Mui-selected": {
                    bgcolor: PRIMARY_COLOR,
                    color: "#fff",
                    boxShadow: "0 4px 6px rgba(0, 123, 255, 0.3)",
                    "&:hover": { bgcolor: "#0056b3" },
                  },
                  "&:hover": { bgcolor: "#f0f8ff" },
                  transition: "all 0.2s",
                }}
              >
                <ListItemIcon>
                  <Icon size={20} color={isSelected ? "#fff" : PRIMARY_COLOR} />
                </ListItemIcon>
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? "bold" : "normal",
                    color: isSelected ? "#fff" : "#333",
                  }}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>

      <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
        <Button
          fullWidth
          onClick={handleLogout}
          startIcon={<LogOut size={16} />}
          variant="contained"
          sx={{
            textTransform: "none",
            bgcolor: DANGER_COLOR,
            "&:hover": { bgcolor: "#a71d2a" },
            borderRadius: 2,
          }}
        >
          Logout
        </Button>
      </Box>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "#fff",
          borderBottom: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ px: 3, py: 1 }}>
          <div className="flex items-center gap-2 flex-grow">
            {isMobile && (
              <IconButton edge="start" onClick={handleDrawerToggle} sx={{ color: PRIMARY_COLOR }}>
                <MenuIcon />
              </IconButton>
            )}
            <GraduationCap size={28} color={PRIMARY_COLOR} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#333" }}>
              INSTITUTION CMS
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333" }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: PRIMARY_COLOR, fontWeight: "medium" }}>
                {user?.role === "super_admin" ? "Super Administrator" : "Administrator"}
              </Typography>
            </div>
            {!isMobile && (
              <Button
                onClick={handleLogout}
                startIcon={<LogOut size={18} />}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: DANGER_COLOR,
                  color: DANGER_COLOR,
                  "&:hover": {
                    bgcolor: DANGER_COLOR,
                    color: "#fff",
                    borderColor: DANGER_COLOR,
                  },
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Logout
              </Button>
            )}
          </div>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: "full",
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: "240",
            boxSizing: "border-box",
            borderRight: "none",
            boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
            top: 64,
            height: "calc(100% - 64px)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          marginTop: "64px",
          ml: { lg: "240px" },
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <div className="w-full">{renderTabContent()}</div>
      </Box>

      <ReportModal
        open={reportModalOpen}
        handleClose={handleCloseReportModal}
        reportType={reportRange}
        reportData={reportModalData ? { [reportRange]: reportModalData } : {}}
        formatCurrency={formatCurrency}
      />
      {reportLoading && (
        <div className="fixed bottom-4 right-4 bg-white shadow p-3 rounded-md text-sm">
          Generating {reportRange} report...
        </div>
      )}
      {reportError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 shadow p-3 rounded-md text-sm text-red-700">
          {reportError}
        </div>
      )}
    </div>
  )
}

export default AdminDashboard